/**
 * @file guideSelectors.js
 * @description Pure selection logic for the onboarding feature guide.
 *
 * Kept separate from both the content (guideSteps.js) and the presentation
 * (FeatureGuideDialog.js) so the capability rules are trivially unit-testable
 * and have a single responsibility. No React, no side effects.
 */

import { labelSourceFor } from "../components/leads/labels/labelsModel";

/**
 * Maps a content capability key → a predicate over the user object.
 *
 * Most predicates read exactly one capability flag already surfaced on the
 * user (see backend /users/me). Adding a new gateable capability is a one-line
 * change here; content files reference the key by string.
 *
 * `no_profession` USED TO LIVE HERE and is deliberately gone (PR E): the tour's
 * own "set a profession" step was the third surface asking the same question,
 * and it is the one that could not act on the answer - it had no CTA, because a
 * CTA that early let a user leave the tour after two steps and never see it
 * again. The welcome dialog and the inline empty-state form are now the two
 * asks, and both submit. An unknown key fails closed, so the deleted predicate
 * cannot leak a step even if a content file still referenced it.
 */
const CAPABILITY_PREDICATES = {
  bi: (user) => Boolean(user?.has_active_bi_monitor),
  intervention: (user) => Boolean(user?.intervention_enabled),
  admin: (user) => Boolean(user?.is_admin),
  recommender: (user) => Boolean(user?.is_recommender),
  // follow-up reminders: Professional/Enterprise + any trialing user (not paid Solo)
  followup: (user) => Boolean(user?.follow_up_eligible),
  // additional notification recipients: a paid Professional/Enterprise feature
  // (Solo/Trial get 0 extra recipients, so the card is hidden from them).
  additional_emails: (user) =>
    ["professional", "enterprise"].includes(user?.user_type),
  // business-website builder: Professional/Enterprise + trialing users (backend
  // centralizes the tier logic into the single `website_eligible` flag).
  website: (user) => Boolean(user?.website_eligible),
  // supporter network: every lead-eligible tier (trial 1 / solo 2 / pro 5 /
  // ent 20 contacts). The backend derives the single `supporter_network_eligible`
  // flag from can_use_supporter_network, so no tier literal appears here.
  supporters: (user) => Boolean(user?.supporter_network_eligible),
  // trial LIFECYCLE, not a tier: a trialing user is user_type='solo' with
  // status='trial', so this reads `status` and never a user_type literal
  // (see the entitlements rule in CLAUDE.md). Gates trial-only guidance, which
  // stops being true the moment the trial converts or lapses.
  trial: (user) => user?.status === "trial",
  // lead labels: the dashboard's OWN labels gate (Dashboard.js
  // `labelsAllowedFor`), read through the one predicate that defines it -
  // signed in and not lapsed. "mine" because the tour describes the user's own
  // feed; an admin gets labels there too. Imported, never re-derived.
  labels: (user) => labelSourceFor(user, "mine") === "viewer",
};

/**
 * Does the user satisfy every capability a step requires?
 *
 * Admins are NOT auto-granted feature capabilities — a step that requires
 * 'bi' still needs has_active_bi_monitor. (Admins reach those pages via the
 * admin override in the navbar, but the guide describes a user's own
 * features, so we gate on the real capability flags.) Unknown keys fail
 * closed (the step is hidden) so a typo can never leak a card to everyone.
 *
 * @param {object|null} user - the current user (capability flags included)
 * @param {string[]} [requires] - capability keys; all must hold
 * @returns {boolean}
 */
function userSatisfies(user, requires) {
  if (!requires || requires.length === 0) return true;
  return requires.every((key) => {
    const predicate = CAPABILITY_PREDICATES[key];
    return typeof predicate === "function" ? predicate(user) : false;
  });
}

/**
 * Filter guide steps down to those the given user is allowed to see, preserving
 * source order.
 *
 * @param {Array<object>} steps - the full GUIDE_STEPS list
 * @param {object|null} user - the current user
 * @returns {Array<object>} the visible subset (never null)
 */
export function selectVisibleSteps(steps, user) {
  if (!Array.isArray(steps)) return [];
  return steps.filter((step) => userSatisfies(user, step.requires));
}

/**
 * Decide what to do with the guide on an authenticated load. Pure so the
 * branching is unit-testable in isolation from React/effects.
 *
 * Returns one of:
 *   'open'      — the user hasn't seen this version and has visible steps.
 *   'mark-seen' — hasn't seen it but has NO visible steps; record the version
 *                 anyway so we don't re-evaluate on every load.
 *   'skip'      — still loading, logged out, already seen this version, or
 *                 already auto-opened this session.
 *
 * Ordering rule (2026-08 swap, REVISED by PR E): the tour used to own the
 * first-login slot unconditionally, because it was the only thing that could
 * tell a brand-new user that a profession must be set before anything can be
 * matched. The WELCOME DIALOG now does that, and it can also take the answer -
 * so login 1 belongs to the dialog and the tour moves to login 2.
 *
 * `welcomeOwnsSession` is what enforces it, and it must be TRUE both while the
 * dialog is due AND for the rest of a session in which it has already been
 * shown: a user who closes the dialog has, in that same tick, an optimistically
 * stamped `onboarding_welcome_seen_at`, so "is the dialog due" alone would flip
 * to false and hand the tour the remainder of login 1 - which is exactly the
 * stacked-modal pattern this queue exists to prevent. GuideProvider passes
 * `shouldShowWelcome(user) || welcomeShownThisSession()`.
 *
 * IMPORTANT: it returns 'skip', NOT 'mark-seen'. Marking the version seen here
 * would satisfy the three promos' gate (`>= currentVersion`) and hand login 2
 * to a promo instead of to the tour, permanently skipping the tour for every
 * new user. 'skip' leaves the version untouched, so the tour is simply due on
 * the next session - which is the intended queue.
 *
 * The website-builder promo waits until the guide version has been seen (see
 * `shouldShowWebsitePromo`), making those two conditions exact complements:
 * this function only ever acts while the stored version is BELOW current, and
 * the promo only ever shows at or above it, so they can never fire together.
 *
 * @param {object} args
 * @param {boolean} args.loading           - auth still resolving
 * @param {boolean} args.isAuthenticated
 * @param {object|null} args.user
 * @param {number} args.visibleStepsCount  - steps the user can see
 * @param {boolean} args.alreadyAutoOpened - session guard already set
 * @param {number} args.currentVersion     - CURRENT_GUIDE_VERSION
 * @param {boolean} [args.welcomeOwnsSession] - the welcome dialog has this
 *                                          session's modal slot
 * @returns {'open'|'mark-seen'|'skip'}
 */
export function decideAutoOpen({
  loading,
  isAuthenticated,
  user,
  visibleStepsCount,
  alreadyAutoOpened,
  currentVersion,
  welcomeOwnsSession = false,
}) {
  if (loading || !isAuthenticated || !user) return "skip";
  if ((user.onboarding_guide_version || 0) >= currentVersion) return "skip";
  if (alreadyAutoOpened) return "skip";
  if (welcomeOwnsSession) return "skip";
  return visibleStepsCount > 0 ? "open" : "mark-seen";
}

/**
 * Decide whether the website-builder promo modal may open on this load.
 *
 * The promo is the SECOND act of onboarding: it stands down until the
 * onboarding tour has had its turn. Pure, so the mutually-exclusive pair
 * (this + decideAutoOpen) is testable in one place. A THIRD act now queues
 * behind it - see shouldShowSupporterAnnounce, which yields to this one.
 *
 * Why gate on the guide VERSION rather than a "second login" stamp: the auth
 * dependency rotates `previous_login_at` roughly 15 minutes into the FIRST
 * session, so it is not a reliable session counter. The stored guide version
 * is, and it is the exact complement of decideAutoOpen's own condition.
 *
 * `guideActed` closes the one same-session hole: decideAutoOpen can return
 * 'mark-seen' (a user with zero visible steps), which writes the version
 * WITHOUT ever showing a dialog and without setting `guideAutoOpened`. That
 * write is optimistic and synchronous, so `tourSeen` would flip mid-session
 * and this promo would steal the tour's own login-1 slot. The guide's session
 * key is set on BOTH the 'open' and 'mark-seen' branches (and never on
 * 'skip'), and it is written before the re-render that the optimistic user
 * update causes — so honouring it closes the race, and a returning user, who
 * never sets the key, still sees the promo.
 *
 * @param {object} args
 * @param {object|null} args.user
 * @param {boolean} args.guideAutoOpened - tour auto-opened this session (state)
 * @param {boolean} args.guideActed      - guide acted this session (session key)
 * @param {boolean} args.promoSeen       - promo already dismissed (localStorage)
 * @param {number} args.currentVersion   - CURRENT_GUIDE_VERSION
 * @returns {boolean}
 */
export function shouldShowWebsitePromo({
  user,
  guideAutoOpened,
  guideActed,
  promoSeen,
  currentVersion,
}) {
  if (!user || !user.website_eligible) return false;
  if (promoSeen) return false;
  // Exact complement of decideAutoOpen: it only acts BELOW currentVersion.
  if ((user.onboarding_guide_version || 0) < currentVersion) return false;
  if (guideAutoOpened || guideActed) return false;
  return true;
}

/**
 * Decide whether the supporter-network "what's new" dialog may open on this load.
 *
 * THIRD in the login queue, and the ordering is the whole point:
 *   1. the tour              (decideAutoOpen — owns login #1 outright)
 *   2. the website promo     (shouldShowWebsitePromo)
 *   3. this announcement
 *   4. the push announcement (shouldShowPushAnnounce — yields to this one)
 * At most ONE promo/announce dialog may appear per login. Stacking two modals on
 * a returning user reads as an interruption, not as news.
 *
 * The tour gate is byte-identical to shouldShowWebsitePromo's (guide version at
 * or above current, plus both same-session guards) — see that function for why
 * the stored version, and not a login counter, is the reliable signal.
 *
 * `websitePromoSeen` is honoured ONLY for a website-eligible user. A user who
 * can never see the website promo also never writes its localStorage key, so
 * waiting on that key unconditionally would keep this dialog suppressed
 * FOREVER for exactly the users (paid Solo) that the supporter network is being
 * announced to. Eligibility is therefore part of the yield condition, not just
 * the key.
 *
 * @param {object} args
 * @param {object|null} args.user
 * @param {boolean} args.guideAutoOpened   - tour auto-opened this session (state)
 * @param {boolean} args.guideActed        - guide acted this session (session key)
 * @param {boolean} args.announceSeen      - this dialog was dismissed (localStorage)
 * @param {boolean} args.websitePromoSeen  - website promo dismissed (localStorage)
 * @param {number} args.currentVersion     - CURRENT_GUIDE_VERSION
 * @returns {boolean}
 */
export function shouldShowSupporterAnnounce({
  user,
  guideAutoOpened,
  guideActed,
  announceSeen,
  websitePromoSeen,
  currentVersion,
}) {
  if (!user || !user.supporter_network_eligible) return false;
  if (announceSeen) return false;
  // Exact complement of decideAutoOpen: it only acts BELOW currentVersion.
  if ((user.onboarding_guide_version || 0) < currentVersion) return false;
  if (guideAutoOpened || guideActed) return false;
  // Yield to the website promo while it still has a turn coming.
  if (user.website_eligible && !websitePromoSeen) return false;
  return true;
}

/**
 * Decide whether the push-notifications "what's new" dialog may open on this load.
 *
 * FOURTH in the login queue (the labels announce now queues behind it):
 *   1. the tour               (decideAutoOpen — owns login #1 outright)
 *   2. the website promo      (shouldShowWebsitePromo)
 *   3. the supporter announce (shouldShowSupporterAnnounce)
 *   4. this announcement
 *   5. the labels announce    (shouldShowLabelsAnnounce - yields to this one)
 * At most ONE promo/announce dialog may appear per login.
 *
 * Unlike the three above it, this one has NO capability gate: push notifications
 * are the primary channel for every user, so `requires` on the matching tour step
 * is [] and there is no eligibility flag to read. That makes the yield conditions
 * the ONLY thing keeping it off another dialog's turn, so both are written the
 * same way as shouldShowSupporterAnnounce's: each is honoured only for a user who
 * can actually SEE the dialog being yielded to. Waiting on a localStorage key
 * unconditionally would suppress this dialog FOREVER for the users who can never
 * write that key (a paid Solo user never sees the website promo; a lead-ineligible
 * user never sees the supporter announce).
 *
 * The tour gate is byte-identical to the other two (stored guide version at or
 * above current, plus both same-session guards) — see shouldShowWebsitePromo for
 * why the stored version, and not a login counter, is the reliable signal.
 *
 * IMPORTANT — the two `*Seen` flags this takes must be the values observed at the
 * START of the session, not live localStorage reads. A dialog writes its key when
 * it is DISMISSED, so a live read cannot tell "dismissed weeks ago" from
 * "dismissed sixty seconds ago on this very screen", and this dialog would take
 * a second turn in the same login. PushNotificationsAnnounce snapshots both keys
 * once per session and passes the snapshot in; keeping the impurity there leaves
 * this function pure and testable.
 *
 * @param {object} args
 * @param {object|null} args.user
 * @param {boolean} args.guideAutoOpened        - tour auto-opened this session (state)
 * @param {boolean} args.guideActed             - guide acted this session (session key)
 * @param {boolean} args.announceSeen           - this dialog was dismissed (localStorage)
 * @param {boolean} args.websitePromoSeen       - website promo dismissed AT SESSION START
 * @param {boolean} args.supporterAnnounceSeen  - supporter announce dismissed AT SESSION START
 * @param {number} args.currentVersion          - CURRENT_GUIDE_VERSION
 * @returns {boolean}
 */
export function shouldShowPushAnnounce({
  user,
  guideAutoOpened,
  guideActed,
  announceSeen,
  websitePromoSeen,
  supporterAnnounceSeen,
  currentVersion,
}) {
  if (!user) return false;
  if (announceSeen) return false;
  // Exact complement of decideAutoOpen: it only acts BELOW currentVersion.
  if ((user.onboarding_guide_version || 0) < currentVersion) return false;
  if (guideAutoOpened || guideActed) return false;
  // Yield to the website promo while it still has a turn coming.
  if (user.website_eligible && !websitePromoSeen) return false;
  // …and to the supporter announcement, for the same reason.
  if (user.supporter_network_eligible && !supporterAnnounceSeen) return false;
  return true;
}

/**
 * Decide whether the lead-labels "what's new" dialog may open on this load.
 *
 * FIFTH and LAST in the login queue:
 *   1. the tour               (decideAutoOpen - owns login #1 outright)
 *   2. the website promo      (shouldShowWebsitePromo)
 *   3. the supporter announce (shouldShowSupporterAnnounce)
 *   4. the push announce      (shouldShowPushAnnounce)
 *   5. this announcement
 * At most ONE promo/announce dialog may appear per login.
 *
 * Gates, in order:
 *   * the labels gate itself - the same predicate the dashboard uses
 *     (`labelSourceFor(user, "mine") === "viewer"`: signed in, not lapsed);
 *   * `labelsAvailable` - the dashboard's labels list has LOADED and holds at
 *     least one label. The copy talks about labels that are already there, so a
 *     failed seed or an empty list must not get a dialog about an empty feature;
 *   * EXISTING users only - `created_at` strictly before `launchCutoff`. A
 *     missing or unparseable `created_at` fails closed. Newer accounts learn
 *     labels from the `lead-labels` tour step instead;
 *   * the tour gate, byte-identical to the three siblings (stored guide version
 *     at or above current, plus both same-session guards);
 *   * the yields. Website and supporter are honoured only for a user eligible
 *     for them (an ineligible user never writes their keys, so an unconditional
 *     wait would suppress this dialog forever). The push announce has NO
 *     eligibility gate, so every user yields to it until it has been seen.
 *
 * IMPORTANT - the three earlier `*Seen` flags must be the values observed at
 * the START of the session (LabelsAnnounce snapshots them in sessionStorage),
 * never live reads: a sibling writes its key when it is DISMISSED, and a live
 * read would open this dialog right behind the one the user just closed.
 *
 * @param {object} args
 * @param {object|null} args.user
 * @param {boolean} args.labelsAvailable        - labels loaded AND at least one
 * @param {boolean} args.guideAutoOpened        - tour auto-opened this session (state)
 * @param {boolean} args.guideActed             - guide acted this session (session key)
 * @param {boolean} args.announceSeen           - this dialog was dismissed (localStorage)
 * @param {boolean} args.websitePromoSeen       - website promo dismissed AT SESSION START
 * @param {boolean} args.supporterAnnounceSeen  - supporter announce dismissed AT SESSION START
 * @param {boolean} args.pushAnnounceSeen       - push announce dismissed AT SESSION START
 * @param {number} args.currentVersion          - CURRENT_GUIDE_VERSION
 * @param {number} args.launchCutoff            - epoch ms; accounts created at or
 *                                                after it are "new"
 * @returns {boolean}
 */
export function shouldShowLabelsAnnounce({
  user,
  labelsAvailable,
  guideAutoOpened,
  guideActed,
  announceSeen,
  websitePromoSeen,
  supporterAnnounceSeen,
  pushAnnounceSeen,
  currentVersion,
  launchCutoff,
}) {
  if (!user) return false;
  if (labelSourceFor(user, "mine") !== "viewer") return false;
  if (!labelsAvailable) return false;
  if (announceSeen) return false;
  // Existing users only. Fail closed on anything that is not a real instant.
  const createdAt =
    typeof user.created_at === "string" ? Date.parse(user.created_at) : NaN;
  if (!Number.isFinite(createdAt) || !Number.isFinite(launchCutoff)) return false;
  if (createdAt >= launchCutoff) return false;
  // Exact complement of decideAutoOpen: it only acts BELOW currentVersion.
  if ((user.onboarding_guide_version || 0) < currentVersion) return false;
  if (guideAutoOpened || guideActed) return false;
  // Yield to every earlier dialog that still has a turn coming.
  if (user.website_eligible && !websitePromoSeen) return false;
  if (user.supporter_network_eligible && !supporterAnnounceSeen) return false;
  if (!pushAnnounceSeen) return false;
  return true;
}

// Exported for unit testing only.
export const __testables = { userSatisfies, CAPABILITY_PREDICATES };
